'use client';

import { c as _c } from "react/compiler-runtime";
import { createContext, PropsWithChildren, useCallback, useContext, useMemo } from 'react';
import type { GetProjectDetailedReportsResp, ReportDetailed } from '@/api/generated/model';
import { getNebulyPageSubproductForPathname } from '@/domains/pages/utils/nebuly-page-subproduct';
import { useReportSubproductQueryValue } from '@/domains/reports/hooks/use-report-subproduct';
import { isReportSubproductEnabled } from '@/domains/reports/utils/report-subproduct-availability.utils';
import { getReportSubproduct, resolveReportSubproductForProject } from '@/domains/reports/utils/report-subproduct.utils';
import { sortReportsForSidebar, sortWorkspaceReportsForSidebar } from '@/domains/reports/utils/sidebar-reports.utils';
import { useAuthContext } from '@/hooks/use-auth';
import { useConfig } from '@/hooks/use-config';
import { useProjectsContext } from '@/providers/projects-provider';
import { isNebulyPagePath } from '@/domains/pages/constants';
import { usePathname } from 'next/navigation';
import { getDetailedReportsQueryKey, invalidateDetailedReports, useFetchDetailedReports } from '@/models/reports/useFetchDetailedReports';
import { useQueryClient } from '@tanstack/react-query';
type ProjectReportsContextValue = {
  projectId: string | undefined;
  detailedReportsResp: GetProjectDetailedReportsResp | undefined;
  favoriteReportsResp: GetProjectDetailedReportsResp | undefined;
  projectReportsResp: GetProjectDetailedReportsResp | undefined;
  mergedProjectReports: ReportDetailed[];
  /** Favorites and private sidebar sections — never scoped by subproduct. */
  mergedPersonalSidebarReports: ReportDetailed[];
  favoriteIdSet: Set<string>;
  isFavoriteListLoaded: boolean;
  isLoadingFavoriteReports: boolean;
  isLoadingProjectReports: boolean;
  isLoadingPersonalProjectReports: boolean;
  getReportById: (reportId: string) => ReportDetailed | undefined;
  refetchReports: () => void;
  refetchFavoriteReports: () => void;
  refetchAll: () => void;
};
const ProjectReportsContext = createContext<ProjectReportsContextValue | null>(null);
export function ProjectReportsProvider(t0) {
  const $ = _c(51);
  const {
    children
  } = t0;
  const {
    projectId,
    selectedProject
  } = useProjectsContext();
  const {
    isSharedLink
  } = useAuthContext();
  const pathname = usePathname();
  const subproductQueryValue = useReportSubproductQueryValue();
  const originalProjectId = selectedProject?.mappedProjectId ?? projectId;
  const {
    data: projectConfig
  } = useConfig(originalProjectId);
  const shouldFilterWorkspaceBySubproduct = pathname === "/reports" || pathname === "/explore" || pathname === "/taxonomy" || pathname === "/all-reports" || isNebulyPagePath(pathname);
  const canFetchProjectReports = !!projectId && !isSharedLink;
  const queryClient = useQueryClient();
  const {
    data: detailedReportsResp,
    isLoading: isLoadingDetailedReports,
    isPreviousData: isPreviousDetailedReports,
    refetch: refetchDetailedReports
  } = useFetchDetailedReports(projectId, canFetchProjectReports);
  const isDetailedReportsStale = isPreviousDetailedReports;
  let t1;
  if ($[0] !== detailedReportsResp?.reports) {
    t1 = detailedReportsResp?.reports ?? [];
    $[0] = detailedReportsResp?.reports;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const allReports = t1;
  selectedProject?.product;
  let t2;
  bb0: {
    if (!shouldFilterWorkspaceBySubproduct) {
      t2 = allReports;
      break bb0;
    }
    let t3;
    if ($[2] !== pathname || $[3] !== selectedProject?.product || $[4] !== subproductQueryValue) {
      t3 = subproductQueryValue ?? getNebulyPageSubproductForPathname(pathname) ?? resolveReportSubproductForProject(null, selectedProject?.product);
      $[2] = pathname;
      $[3] = selectedProject?.product;
      $[4] = subproductQueryValue;
      $[5] = t3;
    } else {
      t3 = $[5];
    }
    const activeSubproduct = t3;
    if (!isReportSubproductEnabled(activeSubproduct, projectConfig)) {
      let t4;
      if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
        t4 = [];
        $[6] = t4;
      } else {
        t4 = $[6];
      }
      t2 = t4;
      break bb0;
    }
    let t4;
    if ($[7] !== activeSubproduct || $[8] !== allReports) {
      let t5;
      if ($[10] !== activeSubproduct) {
        t5 = report => getReportSubproduct(report) === activeSubproduct;
        $[10] = activeSubproduct;
        $[11] = t5;
      } else {
        t5 = $[11];
      }
      t4 = allReports.filter(t5);
      $[7] = activeSubproduct;
      $[8] = allReports;
      $[9] = t4;
    } else {
      t4 = $[9];
    }
    t2 = t4;
  }
  const workspaceReports = t2;
  let t3;
  if ($[12] !== allReports) {
    t3 = new Set(allReports.filter(_temp).map(_temp2));
    $[12] = allReports;
    $[13] = t3;
  } else {
    t3 = $[13];
  }
  const favoriteIdSet = t3;
  let t4;
  if ($[14] !== workspaceReports) {
    t4 = [...workspaceReports].sort(sortWorkspaceReportsForSidebar);
    $[14] = workspaceReports;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  const mergedProjectReports = t4;
  let t5;
  if ($[16] !== allReports) {
    t5 = [...allReports].sort(sortReportsForSidebar);
    $[16] = allReports;
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  const mergedPersonalSidebarReports = t5;
  let map;
  if ($[18] !== mergedPersonalSidebarReports || $[19] !== mergedProjectReports) {
    map = new Map();
    for (const report_2 of mergedPersonalSidebarReports) {
      map.set(report_2.id, report_2);
    }
    for (const report_3 of mergedProjectReports) {
      map.set(report_3.id, report_3);
    }
    $[18] = mergedPersonalSidebarReports;
    $[19] = mergedProjectReports;
    $[20] = map;
  } else {
    map = $[20];
  }
  const reportsById = map;
  let t6;
  if ($[21] !== reportsById) {
    t6 = reportId => reportsById.get(reportId);
    $[21] = reportsById;
    $[22] = t6;
  } else {
    t6 = $[22];
  }
  const getReportById = t6;
  let t7;
  if ($[23] !== refetchDetailedReports) {
    t7 = () => {
      refetchDetailedReports();
    };
    $[23] = refetchDetailedReports;
    $[24] = t7;
  } else {
    t7 = $[24];
  }
  const refetchReports = t7;
  let t8;
  if ($[25] !== queryClient) {
    t8 = () => {
      invalidateDetailedReports(queryClient);
    };
    $[25] = queryClient;
    $[26] = t8;
  } else {
    t8 = $[26];
  }
  const refetchAll = t8;
  let t9;
  if ($[27] !== detailedReportsResp || $[28] !== workspaceReports) {
    t9 = detailedReportsResp ? {
      reports: workspaceReports,
      sidebar_layout_version: detailedReportsResp.sidebar_layout_version
    } : undefined;
    $[27] = detailedReportsResp;
    $[28] = workspaceReports;
    $[29] = t9;
  } else {
    t9 = $[29];
  }
  const projectReportsResp = t9;
  let t10;
  if ($[30] !== allReports || $[31] !== detailedReportsResp) {
    t10 = detailedReportsResp ? {
      reports: allReports.filter(_temp3),
      sidebar_layout_version: detailedReportsResp.sidebar_layout_version
    } : undefined;
    $[30] = allReports;
    $[31] = detailedReportsResp;
    $[32] = t10;
  } else {
    t10 = $[32];
  }
  const favoriteReportsResp = t10;
  const t11 = detailedReportsResp !== undefined;
  const t12 = !projectId || isLoadingDetailedReports || isDetailedReportsStale;
  const t13 = !projectId || isLoadingDetailedReports || isDetailedReportsStale;
  const t14 = !projectId || isLoadingDetailedReports || isDetailedReportsStale;
  let t15;
  if ($[33] !== detailedReportsResp || $[34] !== favoriteIdSet || $[35] !== favoriteReportsResp || $[36] !== getReportById || $[37] !== mergedPersonalSidebarReports || $[38] !== mergedProjectReports || $[39] !== projectId || $[40] !== projectReportsResp || $[41] !== refetchAll || $[42] !== refetchReports || $[43] !== t11 || $[44] !== t12 || $[45] !== t13 || $[46] !== t14) {
    t15 = {
      projectId,
      detailedReportsResp,
      favoriteReportsResp,
      projectReportsResp,
      mergedProjectReports,
      mergedPersonalSidebarReports,
      favoriteIdSet,
      isFavoriteListLoaded: t11,
      isLoadingFavoriteReports: t12,
      isLoadingProjectReports: t13,
      isLoadingPersonalProjectReports: t14,
      getReportById,
      refetchReports,
      refetchFavoriteReports: refetchReports,
      refetchAll
    };
    $[33] = detailedReportsResp;
    $[34] = favoriteIdSet;
    $[35] = favoriteReportsResp;
    $[36] = getReportById;
    $[37] = mergedPersonalSidebarReports;
    $[38] = mergedProjectReports;
    $[39] = projectId;
    $[40] = projectReportsResp;
    $[41] = refetchAll;
    $[42] = refetchReports;
    $[43] = t11;
    $[44] = t12;
    $[45] = t13;
    $[46] = t14;
    $[47] = t15;
  } else {
    t15 = $[47];
  }
  const value = t15;
  let t16;
  if ($[48] !== children || $[49] !== value) {
    t16 = <ProjectReportsContext.Provider value={value}>{children}</ProjectReportsContext.Provider>;
    $[48] = children;
    $[49] = value;
    $[50] = t16;
  } else {
    t16 = $[50];
  }
  return t16;
}
function _temp3(report_4) {
  return report_4.is_favorite;
}
function _temp2(report_1) {
  return report_1.id;
}
function _temp(report_0) {
  return report_0.is_favorite;
}
export function useProjectReportsContext() {
  const context = useContext(ProjectReportsContext);
  if (!context) {
    throw new Error("useProjectReportsContext must be used within ProjectReportsProvider");
  }
  return context;
}
export { getDetailedReportsQueryKey, invalidateDetailedReports };